/* alAsil purchase identity contract v1. Mirrored in Commerce; parity is tested.
   Pure data helpers: no network, prices, inventory writes or DOM dependency. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.AlAsilPurchaseOptions = api;
})(typeof window === 'object' ? window : globalThis, function () {
  'use strict';
  const PREFIX = '_alasil_preorder_';
  function numeric(value, type) {
    if (value == null || value === '') return '';
    if(typeof value==='number'&&!Number.isSafeInteger(value))throw new Error('Unsafe ' + type + ' identifier');
    const raw = String(value);
    const match = raw.match(new RegExp('^(?:gid://shopify/' + type + '/)?([1-9][0-9]*)$'));
    if (!match) throw new Error('Invalid ' + type + ' identifier');
    return match[1];
  }
  function properties(value) {
    const entries = Array.isArray(value) ? value.map(a => [a.key, a.value]) : Object.entries(value || {});
    return Object.fromEntries(entries.filter(([k,v]) => k && v != null && String(v) !== '')
      .map(([k,v]) => [String(k), String(v)]).sort(([a],[b]) => a < b ? -1 : a > b ? 1 : 0));
  }
  function planId(item) {
    return numeric(item.sellingPlanId || item.selling_plan || item.selling_plan_id || item.selling_plan_allocation?.selling_plan?.id || item.sellingPlanAllocation?.sellingPlan?.id, 'SellingPlan');
  }
  function variantId(item) {return numeric(item.variantId || item.variant_id || item.merchandiseId || item.merchandise?.id || item.id, 'ProductVariant');}
  function parentKey(item) {return String(item.parentLineKey || item.parent_line_key || item.parent_relationship?.parent_key || item.parent_relationship?.parent?.key || '');}
  function parentVariant(item) {return numeric(item.parentVariantId || item.parent_id || item.parent?.merchandiseId || item.parentRelationship?.parent?.merchandise?.id, 'ProductVariant');}
  function identity(item) {
    return JSON.stringify([variantId(item),planId(item),parentKey(item),parentVariant(item),properties(item.properties || item.attributes)]);
  }
  function sameLine(a,b) {try{return identity(a)===identity(b);}catch(_){return false;}}
  function isPreorder(item) {return Object.keys(properties(item.properties || item.attributes)).some(k=>k.startsWith(PREFIX));}
  function assertTerms(item) {
    if (!isPreorder(item)) return;
    const p=properties(item.properties || item.attributes);
    if(!p[PREFIX+'offer'] || !/^[1-9][0-9]*$/.test(p[PREFIX+'revision'] || '')) throw new Error('Preorder terms are incomplete. Review this product before checkout.');
  }
  function fromFormData(form) {
    const result={id:form.get('id'),quantity:Number(form.get('quantity') || 1)}, props=[];
    for(const [key,value] of form.entries()) {
      const match=key.match(/^properties\[(.+)\]$/);
      if(match) props.push({key:match[1],value});
    }
    const p=properties(props);if(Object.keys(p).length)result.properties=p;
    const plan=form.get('selling_plan');if(plan)result.selling_plan=numeric(plan,'SellingPlan');
    if(form.get('parent_line_key'))result.parent_line_key=String(form.get('parent_line_key'));
    if(form.get('parent_id'))result.parent_id=numeric(form.get('parent_id'),'ProductVariant');
    return result;
  }
  function bridgeItems(items) {
    return items.map(item=>{
      const result={variantId:variantId(item),quantity:item.quantity,properties:properties(item.properties),sellingPlanId:planId(item) || null};
      if(item.key)result.key=String(item.key);
      const key=parentKey(item);
      if(key){
        const parents=items.filter(p=>p.key===key);
        const parent=parents.length===1?parents[0]:null;
        if(!parent)throw new Error('The linked product is missing. Review your cart.');
        result.parentVariantId=variantId(parent);
        result.parentLineKey=key;
      }else if(item.parent_relationship){throw new Error('The linked product could not be verified. Review your cart.');}
      assertTerms(result);return result;
    });
  }
  function cartRootIdentity(line) {
    return JSON.stringify([numeric(line.variantId,'ProductVariant'),numeric(line.sellingPlanId,'SellingPlan') || null,properties(line.properties)]);
  }
  function cartLineIdentity(line) {
    return JSON.stringify([cartRootIdentity(line),line.parentIdentity || null]);
  }
  function normalizeCartProjection(raw) {
    const source=Array.isArray(raw?.lines)?raw.lines:[];
    if(source.length>100)throw new Error('Cart has too many lines');
    const lines=[];
    for(const candidate of source){
      let variant;
      try{variant=numeric(candidate?.variantId,'ProductVariant');}catch(error){if(isPreorder(candidate)||candidate?.sellingPlanId||candidate?.parentVariantId||candidate?.parentIdentity)throw error;continue;}
      const quantity=Number(candidate?.quantity);
      if(!variant||quantity===0)continue;
      if(!Number.isSafeInteger(quantity)||quantity<0||quantity>999)throw new Error('Invalid cart quantity');
      const props=properties(candidate.properties);
      if(Object.keys(props).length>50||Object.entries(props).some(([k,v])=>k.length>100||v.length>2048))throw new Error('Cart properties exceed storage limits');
      const line={variantId:variant,quantity,properties:props,sellingPlanId:numeric(candidate.sellingPlanId,'SellingPlan')||null,parentVariantId:numeric(candidate.parentVariantId,'ProductVariant')||null,parentIdentity:candidate.parentIdentity||null};
      if(line.parentIdentity&&(typeof line.parentIdentity!=='string'||line.parentIdentity.length>110000))throw new Error('Invalid parent identity');
      assertTerms(line);lines.push(line);
    }
    for(const line of lines){
      if(!line.parentVariantId&&!line.parentIdentity)continue;
      const parents=lines.filter(p=>!p.parentVariantId&&!p.parentIdentity&&(line.parentIdentity?cartRootIdentity(p)===line.parentIdentity:p.variantId===line.parentVariantId));
      const unique=new Map(parents.map(p=>[cartRootIdentity(p),p]));
      if(unique.size!==1)throw new Error('Ambiguous or missing nested cart parent');
      const parent=[...unique.values()][0];
      if(line.parentVariantId&&line.parentVariantId!==parent.variantId)throw new Error('Nested cart parent mismatch');
      line.parentVariantId=parent.variantId;line.parentIdentity=cartRootIdentity(parent);
    }
    const byId=new Map();
    for(const line of lines){const key=cartLineIdentity(line),previous=byId.get(key);if(previous){previous.quantity+=line.quantity;if(previous.quantity>999)throw new Error('Cart quantity exceeds limit');}else byId.set(key,line);}
    return {lines:[...byId.values()].sort((a,b)=>cartLineIdentity(a).localeCompare(cartLineIdentity(b)))};
  }
  function canUseLegacyCheckoutFallback(cart) {
    return Array.isArray(cart?.items) && cart.items.length > 0 && cart.items.every(item => {
      try {return !planId(item) && !parentKey(item) && !parentVariant(item) && !item.parent_relationship && !Object.keys(properties(item.properties)).length;}
      catch (_) {return false;}
    });
  }
  return Object.freeze({cartRootIdentity,cartLineIdentity,normalizeCartProjection,canUseLegacyCheckoutFallback,version:2,PREFIX,numeric,properties,planId,variantId,parentKey,parentVariant,identity,sameLine,isPreorder,assertTerms,fromFormData,bridgeItems});
});
